import { useCallback, useEffect, useRef } from 'react';
import { snakeCase } from 'lodash-es';

import { useInView } from 'react-intersection-observer';
import { trackV2 } from 'utils/analytics';

const useFeedAnalytics = ({
  page,
  entity,
  entityId,
  athleteProfileId,
  feedType,
  cursorData,
  clubId,
  viewingAthleteId,
  activityIds,
  element
}) => {
  const props = useCallback(() => {
    const entityName = snakeCase(entity);
    const inGroup = entityName === 'group_activity';
    const source =
      feedType === 'club' && page === 'club' ? 'club_detail' : feedType;
    const hash = {
      in_group: inGroup,
      source,
      viewing_athlete_id: viewingAthleteId,
      rank: cursorData.rank
    };
    if (feedType === 'club' || feedType === 'clubs_feed') {
      hash.club_id = clubId;
    }
    if (feedType === 'profile') {
      hash.profile_athlete = athleteProfileId;
    }
    if (entityName === 'activity') {
      hash.activity_id = entityId;
    }
    if (entityName === 'post') {
      hash.post_id = entityId;
    }
    if (entityName === 'club') {
      hash.club_id = entityId;
    }
    if (entityName === 'challenge') {
      hash.challenge_id = entityId;
    }
    if (entityName === 'group_activity') {
      hash.headline_activity_id = entityId;
      hash.group_activity_ids = activityIds;
    }
    if (entityName === 'fancy_promo' || feedType === 'simple_promo') {
      // TODO: Potentially change this to some other id since there is no entity id
      hash.trackable_id = entityId;
    }
    if (entityName === 'performance_goal_created') {
      hash.performance_goal_id = entityId;
    }
    return hash;
  }, [
    clubId,
    entity,
    entityId,
    cursorData.rank,
    viewingAthleteId,
    page,
    athleteProfileId,
    feedType,
    activityIds
  ]);

  const trackAnalytics = useCallback(
    (elementProps) => {
      const feedEntityCardProps = {
        category: 'feed',
        page: snakeCase(entity)
      };
      trackV2({
        ...feedEntityCardProps,
        ...elementProps,
        properties: {
          ...elementProps.properties,
          ...props()
        }
      });
    },
    [entity, props]
  );

  // intersectionObserverRef is a tag placed on any feed entry to keep track of
  // its inView status
  const [intersectionObserverRef, inView] = useInView({
    threshold: 0.5 // same as android (50%)
  });

  // only report inView status for any re-renders after the initial
  // component mount
  const isFirstRenderRef = useRef(true);

  // report screen enter/exit when the inView status of an entry changes
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      trackAnalytics({
        element,
        action: inView ? 'screen_enter' : 'screen_exit'
      });
    } else {
      // component has now been mounted. Any changes to inViewStatus will now
      // log enter/exit analytics events
      isFirstRenderRef.current = false;
    }
  }, [inView, entityId, trackAnalytics, element]);

  return {
    isFirstRenderRef,
    intersectionObserverRef,
    trackAnalytics
  };
};

export default useFeedAnalytics;
